import { Pipe, PipeTransform } from "@angular/core";
import { isCombiningMark, normalizeForSearchWithMap, shouldPreserveSearchPunctuation } from "@shared/utils";

export interface HighlightPart {
    text: string;
    isMatch: boolean;
}

// End of the source character that starts at `lastStart`, including any combining marks that follow it.
function matchEnd(source: string, lastStart: number): number {
    let end = lastStart + ((source.codePointAt(lastStart) ?? 0) > 0xffff ? 2 : 1);
    while (end < source.length && isCombiningMark(source[end])) end++;
    return end;
}

@Pipe({ name: "highlightMatch", pure: true })
export class HighlightMatchPipe implements PipeTransform {
    transform(text: string | null | undefined, term: string): HighlightPart[] {
        if (!term || !text) return [{ text: text ?? "", isMatch: false }];

        const source = text.normalize("NFC");
        const preservePunctuation = shouldPreserveSearchPunctuation(term);
        const { text: nSource, sourceIndexes } = normalizeForSearchWithMap(source, preservePunctuation, true);
        // Same function for the term as for the source, so both sides fold identically (for example Greek final sigma).
        const nTerm = normalizeForSearchWithMap(term, preservePunctuation, true).text;
        if (!nTerm) return [{ text: source, isMatch: false }];

        const parts: HighlightPart[] = [];

        // The normalized text can differ in length from the source (ligatures expand, apostrophes drop),
        // so match positions are translated back to source positions before slicing.
        let from = 0;
        let idx = nSource.indexOf(nTerm);
        while (idx !== -1) {
            const start = Math.max(sourceIndexes[idx], from);
            const end = matchEnd(source, sourceIndexes[idx + nTerm.length - 1]);
            if (start > from) parts.push({ text: source.slice(from, start), isMatch: false });
            if (end > start) parts.push({ text: source.slice(start, end), isMatch: true });
            from = Math.max(end, from);
            idx = nSource.indexOf(nTerm, idx + nTerm.length);
        }
        if (from < source.length) parts.push({ text: source.slice(from), isMatch: false });
        return parts;
    }
}
